// Catalog.ID Client — Reprint Recovery Sheets
// ============================================================================
// Print sheets of the set the account ALREADY has (registration-restructure
// §16.6). The set lives in the key bag as the `recovery_kit` entry, so any
// sheet type can be evaluated again at any time and the paper that comes out
// combines with the paper printed at registration.
//
// What it is for, in the member's terms: a replacement for a custodian who lost
// theirs; a new custodian for a sheet type; and the distribution round the
// product owner wanted — print the kit, keep that print as the safe copy, and
// hand sheets out later, one at a time, with instructions. Before round 9 the
// only way to get more paper was a REISSUE, which orphans every sheet already
// out there.
//
// Three stages, because `printPdf` opens a window and popup activation does not
// survive the awaits (plan §11): supersecret → choose sheets → print.
//
// A reprinted sheet is share-identical and set-identical to the original but
// NOT byte-identical: the bundle fingerprint, the MAC and the printed
// 16-character checksum belong to the print. Nothing compares those across
// sheets (§16.4).

import { createElement } from '@catalog-shell';
import { createSupersecretTextarea } from './supersecret-textarea';
import { downloadBlob } from './pdf-loader';
import { printBooklet } from './booklet/booklet';
import {
    buildRecoveryCardPages, buildRecoveryCardCopySheets, type RecoveryCardContact,
} from './booklet/recovery-card-page';
import { cardTypeLetter, formatVersion, type AccountHeaderData } from './booklet/shared';
import type { RecoveryKitSource } from './recovery-kit-creator';
import {
    planReprintSelection, NO_KIT_ENTRY_MESSAGE, MAX_SHEET_COPIES,
    type ReprintSheetChoice,
} from './recovery-kit-reprint-plan';
import { planRecordReconciliation } from './recovery-kit-record';
import { buildRecoveryKit, type RecoveryKit } from '../crypto/recovery-kit';
import { readRecoveryKitEntry, type RecoveryKitEntryData } from '../crypto/recovery-kit-entry';
import { serializeBundle, bundleFilename, type BundleBlob } from '../crypto/bundle';
import type { PutRecoveryKitRequest, RecoveryKitInfo } from '../api/account';

export interface RecoveryReprintConfig {
    /** The account whose set is being printed — the ORGANIZATION's cid on the
     *  org pane, never the delegate driving it. */
    cid: string;
    /** Account-header line on the printed sheets. */
    accountTypeLabel?: string;
    /** Label above the supersecret box; an organization ceremony must say
     *  WHOSE supersecret it wants. */
    supersecretPrompt?: string;
    /** Verified email/phone printed on every sheet. Personal accounts only. */
    contact?: RecoveryCardContact;
    /** Where the supersecret is verified and the bag comes from — the same
     *  source the creator uses. */
    source: RecoveryKitSource;
    /** The kit record as last read; drives the reconciliation below. */
    record: RecoveryKitInfo | null;
    /** `POST …/recoverykit/bundle`. Omit where no such endpoint exists (the
     *  organization record) — the reconciliation then leaves a matching record
     *  alone rather than resetting its fire-drill date with a PUT. */
    refreshBundle?: (bundleFingerprint: string) => Promise<void>;
    /** `PUT …/recoverykit` — the repair path for a missing or superseded record. */
    recordKit: (req: PutRecoveryKitRequest) => Promise<void>;
    /** Fetch the account's blobs for this print's bundle — see
     *  `RecoveryKitCreatorConfig.collectBlobs` for why an organization omits it. */
    collectBlobs?: () => Promise<BundleBlob[]>;
    /** Called once the reprint has been prepared, so the caller can re-read its
     *  status line. */
    onDone?: () => void;
}

export function createRecoveryReprint(config: RecoveryReprintConfig): HTMLElement {
    const container = createElement('div', { className: 'cid-recovery-kit-creator' });
    renderSupersecretStage(container, config);
    return container;
}

// ── Stage 1: the supersecret opens the bag, the bag holds the set ───────────

function renderSupersecretStage(container: HTMLElement, config: RecoveryReprintConfig): void {
    container.innerHTML = '';

    container.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Print more sheets of the recovery set you already have. The sheets that come out '
            + 'match the ones printed with your kit: any of them combines with any other, so a replacement '
            + 'sheet works alongside the ones your custodians already hold. Nothing that has been handed '
            + 'out stops working.',
    }));

    const ssField = createElement('div', { className: 'cid-auth-field' });
    ssField.appendChild(createElement('label', {
        className: 'cid-wizard-text',
        textContent: config.supersecretPrompt
            ?? 'Supersecret (verified against your account, then split across the sheets):',
    }));
    const ss = createSupersecretTextarea();
    ssField.appendChild(ss.element);
    container.appendChild(ssField);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    container.appendChild(errorEl);

    const nextBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Continue',
    }) as HTMLButtonElement;
    nextBtn.disabled = true;
    ss.onValidityChange(valid => { nextBtn.disabled = !valid; });
    container.appendChild(nextBtn);

    nextBtn.addEventListener('click', async () => {
        errorEl.classList.remove('cid-pane-error--visible');
        nextBtn.setAttribute('disabled', '');
        nextBtn.textContent = 'Verifying supersecret...';
        try {
            const material = await config.source.resolve(ss.getValue());
            // TRAP (§16.4): `decryptKeyBagWithKey` is a Promise.all — if the bag
            // opened at all, every entry in it decrypted, so a present entry
            // that fails to DECODE is a format problem, not a wrong secret.
            const held = readRecoveryKitEntry(material.bag);
            if (!held) throw new Error(NO_KIT_ENTRY_MESSAGE);
            renderChooseStage(container, config, material, held.data);
        } catch (err) {
            nextBtn.removeAttribute('disabled');
            nextBtn.textContent = 'Continue';
            errorEl.textContent = err instanceof Error ? err.message : 'Could not unlock your account';
            errorEl.classList.add('cid-pane-error--visible');
        }
    });
}

type KitMaterial = Awaited<ReturnType<RecoveryKitSource['resolve']>>;

// ── Stage 2: which sheet types, how many of each ───────────────────────────

function renderChooseStage(
    container: HTMLElement,
    config: RecoveryReprintConfig,
    material: KitMaterial,
    entry: RecoveryKitEntryData,
): void {
    container.innerHTML = '';

    container.appendChild(createElement('h4', {
        className: 'cid-wizard-heading',
        textContent: `Recovery set ${entry.setId.slice(0, 8)}… — ${entry.thresholdK} of ${entry.totalSharesN}`,
    }));
    container.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Choose the sheet types to print and how many copies of each. The purposes are the ones '
            + 'this set was created with and are printed on the sheets; they cannot be changed without '
            + `creating a new set. Any ${entry.thresholdK} DIFFERENT sheet types reconstruct your `
            + 'supersecret — copies of one type carry the same share and count as one.',
    }));

    const includes: HTMLInputElement[] = [];
    const copies: HTMLInputElement[] = [];

    entry.labels.forEach((label, i) => {
        const row = createElement('div', { className: 'cid-kit-card-row' });

        const include = document.createElement('input');
        include.type = 'checkbox';
        include.id = `kit-reprint-include-${i + 1}`;
        include.checked = false;
        row.appendChild(include);
        includes.push(include);

        const letterLabel = createElement('label', {
            className: 'cid-key-field-label cid-kit-card-letter',
            textContent: `Sheet ${cardTypeLetter(i + 1)}:`,
        });
        letterLabel.setAttribute('for', include.id);
        row.appendChild(letterLabel);

        row.appendChild(createElement('span', {
            className: 'cid-key-field-label',
            textContent: 'Purpose:',
        }));
        const labelInput = createElement('input', { className: 'cid-auth-input cid-kit-card-label' });
        labelInput.type = 'text';
        labelInput.value = label;
        labelInput.readOnly = true;
        labelInput.disabled = true;
        row.appendChild(labelInput);

        row.appendChild(createElement('span', {
            className: 'cid-key-field-label',
            textContent: 'Copies:',
        }));
        const copiesInput = createElement('input', { className: 'cid-auth-input cid-kit-card-copies' });
        copiesInput.type = 'number';
        copiesInput.min = '1';
        copiesInput.max = String(MAX_SHEET_COPIES);
        copiesInput.step = '1';
        copiesInput.value = '1';
        row.appendChild(copiesInput);
        copies.push(copiesInput);

        container.appendChild(row);
    });

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    container.appendChild(errorEl);

    const prepareBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Prepare sheets',
    }) as HTMLButtonElement;
    container.appendChild(prepareBtn);

    prepareBtn.addEventListener('click', async () => {
        errorEl.classList.remove('cid-pane-error--visible');
        prepareBtn.setAttribute('disabled', '');
        prepareBtn.textContent = 'Preparing...';
        try {
            const choices: ReprintSheetChoice[] = includes.map((el, i) => ({
                include: el.checked,
                copies: Number(copies[i].value),
            }));
            const { indexes, cardExtraCopies } = planReprintSelection(choices);

            const kit = await buildRecoveryKit({
                entry,
                cid: config.cid,
                supersecret: material.supersecret,
                bag: material.bag,
                publicKeys: material.publicKeys,
                blobs: config.collectBlobs ? await config.collectBlobs() : [],
                indexes,
                cardExtraCopies,
            });

            // Reconcile the RECORD before anything is printed: a custodian
            // verifying a reprinted sheet at /recovery gets "not current" if
            // the node's record names another set, and repairing it is the
            // whole point of having one (§16.7). A record that already names
            // THIS set is left alone here — a PUT would reset the fire-drill
            // date — and its bundle fingerprint is refreshed only if the
            // member actually downloads the new bundle below.
            let recordNote: string | null = null;
            const action = planRecordReconciliation({
                record: config.record,
                entrySetId: entry.setId,
                canRefreshBundle: Boolean(config.refreshBundle),
            });
            if (action === 'replace-record') {
                try {
                    await config.recordKit({
                        recoverySetId: kit.recoverySetId,
                        thresholdK: kit.thresholdK,
                        totalSharesN: kit.totalSharesN,
                        bundleFingerprint: kit.bundle.bundle_fingerprint,
                    });
                } catch (err) {
                    recordNote = err instanceof Error ? err.message : 'Could not update the kit record';
                }
            }

            config.onDone?.();
            renderPrintStage(container, config, kit, recordNote);
        } catch (err) {
            prepareBtn.removeAttribute('disabled');
            prepareBtn.textContent = 'Prepare sheets';
            errorEl.textContent = err instanceof Error ? err.message : 'Could not prepare the sheets';
            errorEl.classList.add('cid-pane-error--visible');
        }
    });
}

// ── Stage 3: print (and, optionally, a fresh encrypted key backup) ─────────

function renderPrintStage(
    container: HTMLElement,
    config: RecoveryReprintConfig,
    kit: RecoveryKit,
    recordNote: string | null,
): void {
    container.innerHTML = '';

    const pageCount = kit.cards.reduce((n, c) => n + 1 + c.extraCopies, 0);
    const letters = kit.cards
        .map(c => cardTypeLetter(c.payload.shareIndex) + (c.extraCopies > 0 ? ` ×${c.extraCopies + 1}` : ''))
        .join(', ');

    container.appendChild(createElement('h4', {
        className: 'cid-wizard-heading',
        textContent: 'Sheets ready to print',
    }));

    if (recordNote) {
        container.appendChild(createElement('div', {
            className: 'cid-pane-error cid-pane-error--visible',
            textContent: `Note: the kit record on this node could not be updated (${recordNote}). `
                + 'The sheets themselves are correct; the verification page may report them as superseded.',
        }));
    }

    container.appendChild(createElement('p', {
        className: 'cid-wizard-warning',
        textContent: `One print job, ${pageCount} page${pageCount === 1 ? '' : 's'} — sheets ${letters}, `
            + 'one per page. Separate the pages as they come off the printer. Send this to a physical '
            + 'printer: a "Save as PDF" destination would leave the sheets in a file on this computer.',
    }));

    container.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'When you hand a sheet over, tell the custodian what it is: one part of a recovery set '
            + `that needs ${kit.thresholdK} different parts together, so their sheet alone reconstructs `
            + 'nothing and is of no use to anyone who takes it. Ask them to keep it somewhere they would '
            + 'keep a passport, and to expect to be asked for it once a year as a test. Write the '
            + 'coordinator\'s name on every sheet — never where the other sheets are kept.',
    }));

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    const list = createElement('div', { className: 'cid-credential-display' });

    const printRow = createElement('div', { className: 'cid-credential-actions' });
    const printBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-credential-action',
        textContent: 'Print recovery sheets',
    });
    printBtn.addEventListener('click', async () => {
        errorEl.classList.remove('cid-pane-error--visible');
        printBtn.setAttribute('disabled', '');
        try {
            const header: AccountHeaderData = {
                cid: config.cid,
                accountTypeLabel: config.accountTypeLabel ?? 'Account',
                version: formatVersion(new Date()),
            };
            // Letterhead, no cover: this is a single-purpose document, the same
            // shape the Unlock Sheet reissue prints (§16.6).
            await printBooklet(
                buildRecoveryCardPages(header, kit, config.contact),
                buildRecoveryCardCopySheets(header, kit, config.contact),
            );
        } catch (err) {
            errorEl.textContent = err instanceof Error ? err.message : 'Print failed';
            errorEl.classList.add('cid-pane-error--visible');
        } finally {
            printBtn.removeAttribute('disabled');
        }
    });
    printRow.appendChild(printBtn);
    list.appendChild(printRow);

    const bundleRow = createElement('div', { className: 'cid-credential-actions' });
    const bundleBtn = createElement('button', {
        className: 'cs-btn cs-btn--default cid-credential-action',
        textContent: 'Download encrypted key backup (bundle)',
    });
    bundleBtn.addEventListener('click', async () => {
        downloadBlob(
            new Blob([serializeBundle(kit.bundle)], { type: 'application/json' }),
            bundleFilename(config.cid, kit.bundle),
        );
        bundleBtn.classList.add('cs-btn--yes');
        // M25's rule: every export the member actually takes becomes the kit
        // record's newest bundle, or the fire drill reports it as stale.
        if (config.refreshBundle) {
            try {
                await config.refreshBundle(kit.bundle.bundle_fingerprint);
            } catch {
                // Non-fatal: the file is already on disk and the sheets stand.
            }
        }
    });
    bundleRow.appendChild(bundleBtn);
    list.appendChild(bundleRow);

    container.appendChild(list);
    container.appendChild(errorEl);

    container.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'These sheets carry the same shares as the ones printed with your kit — only the '
            + 'printed checksum differs, because it identifies the printout rather than the set. The '
            + 'encrypted key backup above is ciphertext and safe to copy anywhere; the annual fire drill '
            + 'needs one.',
    }));
}
